<script lang="ts">
  /**
   * "Add to Now" picker: suggestions, task search and add. `popover` anchors it to
   * `anchor` with Floating UI (NowBar, desktop board); `sheet` shows it as a bottom
   * sheet (phones). Closes itself once Now is full, however it filled up (an add
   * here, or a refresh bringing in another device's changes).
   *
   * Keep it mounted while closed: the search query and results survive a reopen.
   */
  import { onDestroy, tick, untrack } from 'svelte';
  import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';
  import type { NowSuggestion } from '../lib/types';
  import { nowState, addToNow, loadNowSuggestions } from '../lib/stores/now';
  import { jsonHeaders } from '../lib/apiHelpers';
  import MobileSheet from './MobileSheet.svelte';

  interface Props {
    open: boolean;
    mode?: 'popover' | 'sheet';
    /** Popover mode: the element the popover is positioned against. */
    anchor?: HTMLElement | null;
    onClose: () => void;
  }

  let { open, mode = 'popover', anchor = null, onClose }: Props = $props();

  const HIDDEN_STYLE = 'position: fixed; visibility: hidden;';

  let pickerQuery = $state('');
  let suggestions = $state<NowSuggestion[]>([]);
  let searchResults = $state<NowSuggestion[]>([]);
  let loadingPicker = $state(false);
  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  let pickerEl: HTMLDivElement | undefined = $state();
  let pickerInputEl: HTMLInputElement | undefined = $state();
  let pickerStyle = $state(HIDDEN_STYLE);

  let isPopover = $derived(mode === 'popover');

  // eslint-disable-next-line momentium/no-unguarded-effect -- picker lifecycle: load suggestions when opened
  $effect(() => {
    if (!open) return;
    let cancelled = false;
    loadingPicker = true;
    loadNowSuggestions()
      .then((rows) => {
        if (!cancelled) suggestions = rows;
      })
      .finally(() => {
        if (!cancelled) loadingPicker = false;
      });
    return () => {
      cancelled = true;
    };
  });

  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI autoUpdate for the popover
  $effect(() => {
    if (!open || !isPopover || !anchor || !pickerEl) return;
    const reference = anchor;
    const floating = pickerEl;
    // The popover is `visibility: hidden` until its first position lands, and a hidden
    // input can't take focus, so focus the search right after that. (The sheet focuses
    // its `data-sheet-autofocus` input itself.)
    let focused = false;
    return autoUpdate(reference, floating, async () => {
      const result = await computePosition(reference, floating, {
        strategy: 'fixed',
        placement: 'bottom-start',
        middleware: [
          offset(6),
          flip({
            fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
          }),
          shift({ padding: 8 }),
        ],
      });
      pickerStyle = `position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`;
      if (!focused) {
        focused = true;
        await tick();
        pickerInputEl?.focus();
      }
    });
  });

  // A full Now has nothing left to add, and in popover mode the anchor (an open-slot
  // button) usually disappears with the last slot, which would strand the popover.
  // eslint-disable-next-line momentium/no-unguarded-effect -- reacts to the external Now store; only calls onClose, which flips `open` and ends the reaction
  $effect(() => {
    if (!open || $nowState.canAdd) return;
    untrack(() => close({ restoreFocus: true }));
  });

  // MobileSheet handles Esc and backdrop taps for the sheet.
  // eslint-disable-next-line momentium/no-unguarded-effect -- document listeners while the popover is open
  $effect(() => {
    if (!open || !isPopover) return;

    function handleKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close({ restoreFocus: true });
      }
    }

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (pickerEl?.contains(target) || anchor?.contains(target)) return;
      close();
    }

    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  onDestroy(() => {
    if (searchTimer) clearTimeout(searchTimer);
  });

  /**
   * `restoreFocus`: hand focus back to the anchor, since the popover (holding focus)
   * is about to unmount. Not on outside clicks: focus belongs where the user clicked.
   * The sheet returns focus to its trigger itself.
   */
  function close({ restoreFocus = false } = {}) {
    if (restoreFocus && isPopover && anchor?.isConnected) anchor.focus();
    pickerStyle = HIDDEN_STYLE;
    onClose();
  }

  async function handleAdd(taskId: string) {
    const ok = await addToNow(taskId);
    if (ok) {
      pickerQuery = '';
      searchResults = [];
      suggestions = suggestions.filter((row: NowSuggestion) => row.taskId !== taskId);
      // If that filled Now, the canAdd effect closes the picker.
    }
  }

  function handleSearchInput(value: string) {
    pickerQuery = value;
    if (searchTimer) clearTimeout(searchTimer);
    const q = value.trim();
    if (!q) {
      searchResults = [];
      return;
    }
    searchTimer = setTimeout(async () => {
      loadingPicker = true;
      try {
        const response = await fetch(
          `/search?q=${encodeURIComponent(q)}&include_done=false&include_archived=false`,
          { headers: jsonHeaders() },
        );
        if (!response.ok) return;
        const data = await response.json();
        const inNow = new Set($nowState.items.map((item) => item.taskId));
        searchResults = (data.tasks || [])
          .filter(
            (task: { id: string; status: string }) => task.status === 'todo' && !inNow.has(task.id),
          )
          .map(
            (task: {
              id: string;
              projectId: string;
              projectTitle: string;
              title: string;
              priority: string;
              status: string;
            }) => ({
              taskId: task.id,
              projectId: task.projectId,
              projectTitle: task.projectTitle,
              title: task.title,
              priority: task.priority,
              status: task.status,
            }),
          );
      } finally {
        loadingPicker = false;
      }
    }, 200);
  }
</script>

{#snippet pickerBody()}
  <input
    autocomplete="off"
    class="picker-input"
    type="search"
    placeholder="Search tasks…"
    aria-label="Search tasks"
    data-sheet-autofocus
    bind:this={pickerInputEl}
    value={pickerQuery}
    oninput={(e) => handleSearchInput(e.currentTarget.value)}
  />
  <div class="picker-list">
    {#if loadingPicker}
      <p class="picker-empty">Searching…</p>
    {:else if pickerQuery.trim()}
      {#if searchResults.length === 0}
        <p class="picker-empty">No matching incomplete tasks</p>
      {:else}
        {#each searchResults as result (result.taskId)}
          {@render pickerRow(result)}
        {/each}
      {/if}
    {:else if suggestions.length === 0}
      <p class="picker-empty">No suggestions yet. Search for a task.</p>
    {:else}
      <p class="picker-hint">Suggestions</p>
      {#each suggestions as result (result.taskId)}
        {@render pickerRow(result)}
      {/each}
    {/if}
  </div>
{/snippet}

{#snippet pickerRow(result: NowSuggestion)}
  <button type="button" class="picker-row" onclick={() => handleAdd(result.taskId)}>
    <span class="picker-title">{result.title}</span>
    <span class="picker-project">{result.projectTitle}</span>
  </button>
{/snippet}

{#if isPopover}
  {#if open}
    <div
      class="popover picker"
      bind:this={pickerEl}
      style={pickerStyle}
      onmousedown={(e) => e.stopPropagation()}
      role="dialog"
      tabindex="-1"
      aria-label="Add to Now"
    >
      <div class="popover-header">
        <strong>Add to Now</strong>
        <button
          type="button"
          class="close-btn"
          onclick={() => close({ restoreFocus: true })}
          aria-label="Close">×</button
        >
      </div>
      {@render pickerBody()}
    </div>
  {/if}
{:else}
  <MobileSheet
    {open}
    direction="bottom"
    title="Add to Now"
    initialFocus="first"
    onClose={() => close()}
  >
    <div class="sheet-body">
      {@render pickerBody()}
    </div>
  </MobileSheet>
{/if}

<style>
  .popover {
    width: 22rem;
    max-width: calc(100vw - 1rem);
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
    z-index: 80;
    padding: 0.625rem;
  }

  .popover-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    font-size: 0.8125rem;
  }

  .close-btn {
    border: none;
    background: none;
    color: #9ca3af;
    cursor: pointer;
    border-radius: 999px;
    width: 1.1rem;
    height: 1.1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    font-size: 0.75rem;
    flex-shrink: 0;
  }

  .close-btn:hover {
    color: #374151;
    background: #f3f4f6;
  }

  .picker-input {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    padding: 0.375rem 0.5rem;
    font: inherit;
    font-size: 0.8125rem;
    margin-bottom: 0.5rem;
  }

  .picker-list {
    max-height: 16rem;
    overflow-y: auto;
  }

  .picker-row {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    width: 100%;
    border: none;
    background: none;
    text-align: left;
    padding: 0.4rem 0.25rem;
    border-radius: 0.375rem;
    cursor: pointer;
    font: inherit;
  }

  .picker-row:hover {
    background: #f9fafb;
  }

  .picker-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.8125rem;
    color: #111827;
  }

  .picker-project {
    font-size: 0.6875rem;
    color: #9ca3af;
    flex-shrink: 0;
    max-width: 7rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .picker-empty,
  .picker-hint {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0.25rem 0;
  }

  /* Sheet: full-width touch rows, the list scrolls inside the sheet. */
  .sheet-body {
    padding: 0.75rem 1rem 1rem;
  }

  .sheet-body .picker-input {
    font-size: 1rem; /* ≥16px stops iOS zooming on focus */
    padding: 0.625rem 0.75rem;
    border-radius: 0.5rem;
    margin-bottom: 0.75rem;
  }

  .sheet-body .picker-list {
    max-height: none;
    overflow: visible;
  }

  .sheet-body .picker-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.125rem;
    min-height: 3rem;
    padding: 0.5rem 0.25rem;
    border-bottom: 1px solid #f3f4f6;
    border-radius: 0;
  }

  .sheet-body .picker-title {
    font-size: 1rem;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .sheet-body .picker-project {
    font-size: 0.8125rem;
    max-width: none;
  }

  .sheet-body .picker-empty,
  .sheet-body .picker-hint {
    font-size: 0.875rem;
  }
</style>
